import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Modal,
  Pagination,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CommonHeader from "../../../CommonComponents/CommonHeader";
import { CloseOutlined } from "@mui/icons-material";
import { ReactComponent as EditIcon } from "../../../assets/svg/edit.svg";
import { useEffect, useState } from "react";
import { showErrorToast, showSuccessToast } from "../../../toastUtils";
import { getAllSignups, updateSignupStatus } from "../../../api/signup";
import dayjs from "dayjs";
import { convertUTCToIST } from "../../../utils/dateUtils";
import SearchInput from "../../../CommonComponents/SearchInput";
import SelectCommon from "../../../CommonComponents/SelectCommon";
import DateFilterPicker from "../../../CommonComponents/DateFilterPicker";
import ComponentWrapper from "../../../CommonComponents/ComponentWrapper";

const Signup = () => {
  const [loading, setLoading] = useState(false);
  const [paginatedData, setPaginatedData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [dateValue, setDateValue] = useState(null);
  const [statusValue, setStatusValue] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50); // You can change this if needed
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [selectedSignup, setSelectedSignup] = useState(null); // to store selected row
  const [newStatus, setNewStatus] = useState(""); // new status selected

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#F9FAFB",
      color: "#000000",
      fontWeight: 600,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));

  // Fetch data once

  const fetchSignups = async (pageNumber = 1) => {
    setLoading(true);
    try {
      const response = await getAllSignups(pageNumber, pageSize);
      if (response.status_code === 200) {
        const data = response.data.data || [];
        setPaginatedData(data);
        setFilteredData(data);
        setTotalCount(response.data.total_count || 0);
      } else {
        showErrorToast(response.message);
      }
    } catch (error) {
      console.error("Error fetching signups:", error);
      setPaginatedData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSignups(page);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter logic when filters change
  useEffect(() => {
    let filtered = paginatedData;

    if (searchValue) {
      filtered = filtered.filter(
        (item) =>
          item.name.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.recommended_plan.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    if (statusValue !== "all") {
      filtered = filtered.filter((item) => item.status.toLowerCase() === statusValue.toLowerCase());
    }

    if (dateValue) {
      filtered = filtered.filter((item) =>
        dayjs(item.created_at).isSame(dayjs(dateValue), "day")
      );
    }

    setFilteredData(filtered);
  }, [searchValue, statusValue, dateValue, paginatedData]);

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 600,
    bgcolor: "#FFFFFF",
    borderRadius: "16px",
    boxShadow: 24,
    p: 3,
  };

  return (
    <ComponentWrapper>
      {/* <Stack sx={{ maxWidth: "1000px" }}> */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <CommonHeader
          title="Signups"
          subTitle="You can review, audit and change the status of signup on this page."
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
          alignItems: "center",
          justifyContent: 'end'
        }}
      >
        <SearchInput
          placeholder="Search by Name & Plan"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
        <DateFilterPicker
          value={dateValue}
          onChange={setDateValue}
          sx={{ maxWidth: 250 }}
          label="Filter by Date"
        />
        <SelectCommon
          label="Status"
          value={statusValue}
          onChange={(e) => setStatusValue(e.target.value)}
          options={[
            { label: "All", value: "all" },
            { label: "Lead", value: "lead" },
            { label: "Customer", value: "customer" },
          ]}
        />
      </Box>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: "12px" }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <StyledTableCell>Name</StyledTableCell>
              <StyledTableCell>Email</StyledTableCell>
              <StyledTableCell>Phone</StyledTableCell>
              <StyledTableCell>Website</StyledTableCell>
              <StyledTableCell>Recommended Plan</StyledTableCell>
              <StyledTableCell>Registered Date</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell align="center">Action</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No data found.
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((row) => (
                <TableRow key={row.id} hover>
                  <StyledTableCell sx={{ maxWidth: '250px', overflow: 'hidden' }}>{row.name}</StyledTableCell>
                  <StyledTableCell sx={{ maxWidth: '250px', overflow: 'hidden' }}>{row.email}</StyledTableCell>
                  <StyledTableCell sx={{ maxWidth: '200px', overflow: 'hidden' }}>{row.phone}</StyledTableCell>
                  <StyledTableCell>{row.website}</StyledTableCell>
                  <StyledTableCell>{row.recommended_plan}</StyledTableCell>
                  <StyledTableCell>
                    {convertUTCToIST(row.created_at)}
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{
                      textTransform: "capitalize",
                      color:
                        row.status.toLowerCase() === "lead"
                          ? "#00B894"
                          : row.status.toLowerCase() === "customer"
                            ? "#FF946D"
                            : "#6994FF",
                      fontWeight: 600,
                    }}
                  >{row.status}</StyledTableCell>
                  <StyledTableCell align="center">
                    <Tooltip title="Edit" arrow>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedSignup(row);
                          setNewStatus(row.status); // default to current
                          setOpen(true);
                        }}
                        disabled={row.status === "customer"}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </StyledTableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box display="flex" justifyContent="center" mt={3}>
        <Pagination
          count={Math.ceil(totalCount / pageSize)}
          page={page}
          onChange={(event, value) => setPage(value)}
          color="primary"
          shape="rounded"
          variant="outlined"
        />
      </Box>
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={style}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" color="#525455" fontWeight="bold">
              Change Status
            </Typography>
            <IconButton onClick={() => setOpen(false)}>
              <CloseOutlined />
            </IconButton>
          </Box>

          <Typography color="#989898" sx={{ mb: 2 }}>
            Select a new status for this signup.
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* Dropdown to select status */}
          <TextField
            label="Status"
            select
            fullWidth
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
            size="small"
          >
            {["lead", "customer"].map((status) => (
              <MenuItem
                key={status}
                value={status}
                disabled={status === selectedSignup?.status}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </MenuItem>
            ))}
          </TextField>

          <Box gap={2} display="flex" mt={3}>
            <Button
              variant="outlined"
              sx={{
                border: "2px solid #FF6348",
                textTransform: "capitalize",
                fontWeight: "bold",
              }}
              disabled={loading || newStatus === selectedSignup?.status}
              onClick={async () => {
                try {
                  setLoading(true);
                  const response = await updateSignupStatus(
                    selectedSignup.id,
                    newStatus
                  );
                  if (response.status_code === 200) {
                    showSuccessToast("Status updated!");
                    // Optional: Refresh list
                    await fetchSignups(page);
                    setOpen(false);
                  } else {
                    showErrorToast(response.message);
                  }
                } catch (err) {
                  showErrorToast("Failed to update status");
                } finally {
                  setLoading(false);
                }
              }} // You define this function
            >
              {loading ? <CircularProgress size={20} /> : "Update"}
            </Button>
            <Button
              variant="outlined"
              sx={{
                border: "2px solid #CECECE",
                textTransform: "capitalize",
                fontWeight: "bold",
              }}
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
      {/* </Stack> */}
    </ComponentWrapper>
  );
};

export default Signup;
